"use client";
import React, { useCallback, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";

import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "./ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from "./ui/form";
import { Input } from "./ui/input";
import { useTranslations } from "next-intl";
import { Textarea } from "./ui/textarea";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { toast } from "sonner";

let FormSchema = z.object({
  name: z.string().min(2),
  email: z.string().email().min(2),
  phone: z.string().min(5),
  message: z.string().min(10),
});

export default function ContactForm() {
  const { executeRecaptcha } = useGoogleReCaptcha();

  const t = useTranslations("contact");
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      message: "",
    },
  });

  FormSchema = z.object({
    name: z
      .string({ message: t("nameError") })
      .min(2, { message: t("nameError") }),
    email: z
      .string({ message: t("emailError") })
      .email({ message: t("emailError") })
      .min(2, { message: t("emailError") }),
    phone: z.string({ message: t("phoneError") }),
    message: z
      .string({ message: t("messageError") })
      .min(10, { message: t("messageError") }),
  });

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      return;
    }
    return await executeRecaptcha("contactForm");
  }, [executeRecaptcha]);

  useEffect(() => {
    handleReCaptchaVerify();
  }, [handleReCaptchaVerify]);

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    const recaptchaToken = await handleReCaptchaVerify();
    if (!recaptchaToken) {
      form.setError("root", {
        message: "Impossibile caricare il captcha al momento...",
      });
      return;
    }

    try {
      const response = await fetch("/service/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...data, token: recaptchaToken }),
      });

      const responseData = await response.json();
      if (response.ok && responseData.success) {
        toast.info("Email inviata correttamete!");
        form.reset();
      } else {
        toast.error(
          "Al momento non è possibile inviare la mail, riprova più tardi"
        );
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error: unknown) {
      alert("error");
      toast.error(
        "Al momento non è possibile inviare la mail, riprova più tardi"
      );
    }
  }
  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="w-full grid grid-cols-1 gap-2"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input placeholder={t("name")} {...field} />
              </FormControl>
              <FormMessage />
              <FormDescription className="sr-only">{t("name")}</FormDescription>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input placeholder={t("email")} {...field} />
              </FormControl>
              <FormMessage />
              <FormDescription className="sr-only">
                {t("email")}
              </FormDescription>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input placeholder={t("phone")} {...field} />
              </FormControl>
              <FormMessage />
              <FormDescription className="sr-only">
                {t("phone")}
              </FormDescription>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Textarea
                  placeholder={t("message")}
                  {...field}
                  className="max-h-[400px] min-h-[200px]"
                />
              </FormControl>
              <FormDescription className="sr-only">
                {t("message")}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" size="lg" variant="outline">
          {t("sendMessage")}
        </Button>
      </form>
    </Form>
  );
}
